import { Schedule } from "./types";
import { create } from "zustand";

const API_BASE_URL = process.env.SERVER_HOST;

export const apiFetch = (endpoint: string, options: RequestInit) => {
  const url = `${API_BASE_URL}${endpoint}`;
  return fetch(url, options);
};

interface ScheduleStore {
  createSchedule: (
    data: Record<string, any>,
  ) => Promise<{ success: boolean; schedule?: Schedule; error?: string }>;
  getSchedules: (userId: string) => Promise<Schedule[]>;
  deleteSchedule: (
    userId: string,
    scheduleId: string,
  ) => Promise<{ success: boolean; error?: string }>;
  getMessageSentThisMonth: (userId: string) => Promise<{ messagesSent: number; plan: 'free' | 'plus' | 'pro'; maxMessages: number }>;
}

export const useAppStore = create<ScheduleStore>(() => ({
  getMessageSentThisMonth: async (userId) => {
    try {
      const res = await apiFetch(`/api/spent/${userId}`, {});
      const result = await res.json();
      if (res.ok) {
        return result as { messagesSent: number; plan: 'free' | 'plus' | 'pro'; maxMessages: number };
      } else {
        throw new Error(result.error || "Failed to fetch rate limit info");
      }
    } catch (error: any) {
      console.error("Error fetching rate limit info:", error);
      return { messagesSent: 0, plan: 'free', maxMessages: 0 };
    }
  },
  createSchedule: async (data) => {
    try {
      const res = await apiFetch("/api/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      return result;
    } catch (error) {
      console.error("Error creating schedule:", error);
      return { success: false, error: error?.message || "Failed to schedule" };
    }
  },
  getSchedules: async (userId) => {
    try {
      const res = await apiFetch(`/api/schedules/${userId}`);
      const result = await res.json();
      if (res.ok && Array.isArray(result.schedules)) {
        return result.schedules as Schedule[];
      } else {
        throw new Error(result.error || "Failed to fetch schedules");
      }
    } catch (error: any) {
      console.error("Error fetching schedules:", error);
      return [];
    }
  },
  deleteSchedule: async (userId, scheduleId) => {
    try {
      const res = await apiFetch(`/api/schedules/${userId}/${scheduleId}`, {
        method: "DELETE",
      });
      const result = await res.json();
      if (res.ok && result.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error || "Failed to delete schedule",
        };
      }
    } catch (error: any) {
      console.error("Error deleting schedule:", error);
      return {
        success: false,
        error: error?.message || "Failed to delete schedule",
      };
    }
  },
}));
